import { useEffect, useState } from "react";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { Check } from "lucide-react";
import { BrandMark } from "@/brand/BrandMark";
import { BrandHero } from "@/brand/BrandHero";
import { toast } from "sonner";

const NONE = "__none__";

function onlyDigits(s) { return String(s || "").replace(/\D/g, ""); }
function validateCPF(cpf) {
  cpf = onlyDigits(cpf);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  for (let t = 9; t < 11; t++) {
    let sum = 0;
    for (let i = 0; i < t; i++) sum += Number(cpf[i]) * ((t + 1) - i);
    const d = ((sum * 10) % 11) % 10;
    if (d !== Number(cpf[t])) return false;
  }
  return true;
}
function maskCPF(v) {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export default function PublicCheckin() {
  const [pousadas, setPousadas] = useState([]);
  const [form, setForm] = useState({
    nome: "", cpf: "", telefone: "", email: "", empresa: "", pousada_preferida: "", mensagem: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    publicApi.get("/public/pousadas").then((r) => setPousadas(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Informe seu nome completo");
    if (!validateCPF(form.cpf)) return toast.error("CPF inválido. Confira os dígitos.");
    setLoading(true);
    try {
      await publicApi.post("/public/checkin", { ...form, cpf: onlyDigits(form.cpf) });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar. Tente novamente.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <Toaster richColors position="top-right" />
      <header className="relative h-64 md:h-80 overflow-hidden">
        <BrandHero testid="checkin-brand-hero" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-transparent" />
        <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-end">
          <div className="flex items-center gap-3 text-white mb-4">
            <div className="w-10 h-10 rounded-md bg-[#FFFFFF]/90 flex items-center justify-center shadow-sm">
              
              <BrandMark variant="mark" size={28} />
            </div>
            <div className="font-brand-title text-xl text-white font-bold leading-none">Rede Hospeda</div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E9C46A] mb-2 font-semibold">Check-in online — Pessoa Física</div>
          <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight max-w-2xl">
            Bem-vindo à Rede Hospeda
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {submitted ? (
          <div data-testid="checkin-success" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-6 text-[#083A6B]">
              <Check size={28} strokeWidth={1.8} />
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">Confirmação recebida</div>
            <h2 className="font-serif text-3xl text-stone-900 mb-3">Obrigado{form.nome ? `, ${form.nome.split(" ")[0]}` : ""}!</h2>
            <p className="text-stone-600 max-w-md mx-auto leading-relaxed">
              Recebemos os seus dados. Em breve a recepção entrará em contato para confirmar sua reserva.
            </p>
            <p className="text-xs text-stone-500 mt-6">É empresa? <a href="/checkin/empresa" className="text-[#083A6B] underline">Check-in de pessoa jurídica</a></p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-8 md:p-10 space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-2 font-semibold">Seus dados</div>
              <h2 className="font-serif text-3xl text-stone-900">Preencha seu check-in</h2>
              <p className="text-stone-600 mt-2 text-sm">
                Envie os seus dados para agilizar a chegada. A recepção liga seu cadastro à reserva.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome completo *</Label>
                <Input data-testid="input-checkin-nome" value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <Label>CPF *</Label>
                <Input data-testid="input-checkin-cpf" value={form.cpf}
                  onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })}
                  placeholder="000.000.000-00" maxLength={14} />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input data-testid="input-checkin-telefone" value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" data-testid="input-checkin-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Empresa (opcional)</Label>
                <Input data-testid="input-checkin-empresa" value={form.empresa}
                  onChange={(e) => setForm({ ...form, empresa: e.target.value })} />
              </div>
              <div>
                <Label>Pousada de destino</Label>
                <Select value={form.pousada_preferida || NONE}
                  onValueChange={(v) => setForm({ ...form, pousada_preferida: v === NONE ? "" : v })}>
                  <SelectTrigger data-testid="select-checkin-pousada"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Não informado</SelectItem>
                    {pousadas.map((p) => <SelectItem key={p.id} value={p.nome}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observações (opcional)</Label>
              <Input data-testid="input-checkin-mensagem" value={form.mensagem}
                onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                placeholder="Horário de chegada, pedidos especiais..." />
            </div>
            <Button type="submit" data-testid="btn-checkin-submit" disabled={loading}
              className="w-full h-12 bg-[#083A6B] hover:bg-[#052B50] text-white rounded-md">
              {loading ? "Enviando…" : "Enviar check-in"}
            </Button>
            <div className="text-center">
              <a href="/checkin/empresa" data-testid="link-pj" className="text-xs text-stone-500 hover:text-[#083A6B] underline">
                É empresa? Faça check-in como pessoa jurídica
              </a>
            </div>
          </form>
        )}

        <footer className="text-center text-xs text-stone-500 mt-12">
          Rede Hospeda · Gestão de Pousadas · WhatsApp (11) 91417-5095
        </footer>
      </main>
    </div>
  );
}
